import { useState } from 'react';
import { acceptTerms } from '../lib/terms';
import { signOut } from '../lib/auth';
import type { TermsStatus } from '../../shared/protocol';

/**
 * THU-115 — the blocking agreement, shown once per terms version to a
 * signed-in person before they can use the site.
 *
 * Three things here are load-bearing and each is guarded by
 * `tools/tests/terms-assent.test.js`:
 *
 *  1. The checkbox starts UNCHECKED and the button is disabled until it isn't.
 *     A pre-ticked box is the textbook way to make a clickwrap unenforceable —
 *     it records that a page was rendered, not that a person decided.
 *  2. Both documents are linked, and they open in a new tab. A link that
 *     navigates away would discard the session-in-progress and the user would
 *     come back to this same wall, which teaches people to click through
 *     without reading.
 *  3. "Not now" signs out rather than dismissing. There is no third state: you
 *     are either someone who agreed, or someone who is not signed in. A
 *     dismissable legal gate is decoration.
 */
export function TermsGate({ status, onAccepted }: { status: TermsStatus; onAccepted: () => void }) {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!checked || saving) return;
    setError(null);
    setSaving(true);
    try {
      await acceptTerms(status);
      onAccepted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We could not save that just now.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true" aria-labelledby="terms-gate-h">
      <div className="sheet">
        <div className="card marquee auth-sheet">
          <h2 className="display" id="terms-gate-h">
            Before you start
          </h2>
          <p className="soft">
            Have a read of the two documents that cover using Thursday Night — what you can put on
            the screen, and what we do with the little we keep.
          </p>
          <div className="foot-legal">
            <a href={status.termsUrl} target="_blank" rel="noreferrer noopener">
              Terms of Service
            </a>{' '}
            ·{' '}
            <a href={status.privacyUrl} target="_blank" rel="noreferrer noopener">
              Privacy Policy
            </a>
          </div>
          <label className="terms-agree">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />{' '}
            I've read and agree to the Terms of Service and the Privacy Policy.
          </label>
          {error && <div className="notice error">{error}</div>}
          <button className="btn primary block" onClick={submit} disabled={!checked || saving}>
            {saving ? <span className="spinner" /> : 'Agree and continue'}
          </button>
          <p className="soft small">
            We record your account, the date, and which version you agreed to — nothing else.
          </p>
          <button className="btn link block" onClick={signOut}>
            Not now — sign me out
          </button>
        </div>
      </div>
    </div>
  );
}
